import React, { useState, useEffect } from 'react';
import { db } from '../../utils/firebase';
import { collection, addDoc, getDocs, query } from 'firebase/firestore';
import { Container, Typography, TextField, Button, Card, CardContent, Box, Alert, Divider, Select, MenuItem, FormControl, InputLabel, Chip, Grid, Paper } from '@mui/material';

const TripManager = () => {
  const [trips, setTrips] = useState([]);
  const [tripName, setTripName] = useState('');
  const [tripDescription, setTripDescription] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [error, setError] = useState('');
  const module = 'trip-manager';

  // Helper functions for consistent formatting
  const formatName = (name) => {
    if (!name) return '';
    return name.toLowerCase().split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  useEffect(() => {
    fetchTrips();
    fetchAvailableUsers();
  }, []);

  const fetchTrips = async () => {
    try {
      const q = query(collection(db, `modules/${module}/trips`));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort by createdAt descending (most recent first)
      data.sort((a, b) => new Date(b.createdAt?.toDate?.() || b.createdAt) - new Date(a.createdAt?.toDate?.() || a.createdAt));
      setTrips(data);
    } catch (err) {
      setError('Failed to load trips.');
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const q = query(collection(db, 'modules/trip-manager/users'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAvailableUsers(data);
    } catch (err) {
      setError('Failed to load users.');
    }
  };

  const addUserToTrip = (user) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const removeUserFromTrip = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const createTrip = async () => {
    if (!tripName || selectedUsers.length === 0) {
      setError('Trip name and at least one user required.');
      return;
    }
    const newTrip = {
      name: tripName,
      description: tripDescription,
      participants: selectedUsers.map(u => ({ id: u.id, name: u.name, email: u.email })),
      createdAt: new Date()
    };
    try {
      await addDoc(collection(db, `modules/${module}/trips`), newTrip);
      setTripName('');
      setTripDescription('');
      setSelectedUsers([]);
      fetchTrips();
    } catch (err) {
      setError('Failed to create trip.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>Trip Manager</Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Create trips and assign participants.
      </Typography>
      <Divider sx={{ mb: 4 }} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Create New Trip</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Trip Name"
              value={tripName}
              onChange={(e) => setTripName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={tripDescription}
              onChange={(e) => setTripDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <Typography variant="subtitle1">Select Participants</Typography>
            <FormControl fullWidth>
              <InputLabel>Add User</InputLabel>
              <Select
                value=""
                onChange={(e) => addUserToTrip(availableUsers.find(u => u.id === e.target.value))}
              >
                {availableUsers.filter(u => !selectedUsers.find(su => su.id === u.id)).map((user) => (
                  <MenuItem key={user.id} value={user.id}>{formatName(user.name)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedUsers.map((user) => (
                <Chip
                  key={user.id}
                  label={formatName(user.name)}
                  onDelete={() => removeUserFromTrip(user.id)}
                />
              ))}
            </Box>
            <Button variant="contained" color="primary" onClick={createTrip}>Create Trip</Button>
          </Box>
        </CardContent>
      </Card>
      <Typography variant="h6" gutterBottom>Existing Trips</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        View and manage all trips you've created. Trips are sorted by most recent first.
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {trips.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
          No trips created yet. Create your first trip above!
        </Typography>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {trips.map((trip) => (
            <Grid item xs={12} sm={6} lg={4} xl={4} key={trip.id}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    elevation: 3,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                  },
                  backgroundColor: 'background.paper',
                  height: '100%',
                  maxWidth: 400,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#1b5e20', fontWeight: 'bold' }}>
                  {trip.name}
                </Typography>

                {trip.description && (
                  <>
                    <Divider sx={{ mb: 2, borderColor: 'divider' }} />
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {trip.description}
                    </Typography>
                  </>
                )}

                {!trip.description && <Divider sx={{ mb: 2, borderColor: 'divider' }} />}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'secondary.main', fontWeight: 'medium' }}>
                    Participants ({trip.participants?.length || 0})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {trip.participants?.map((participant) => (
                      <Chip
                        key={participant.id}
                        label={formatName(participant.name)}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          backgroundColor: 'primary.50',
                          fontWeight: 'medium',
                          '&:hover': {
                            backgroundColor: 'primary.100',
                            borderColor: 'primary.dark',
                          },
                        }}
                      />
                    )) || (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No participants
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                    Created: {trip.createdAt?.toDate?.()
                      ? trip.createdAt.toDate().toLocaleDateString()
                      : new Date(trip.createdAt).toLocaleDateString()
                    }
                  </Typography>
                  <Chip
                    label="Active"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: 'success.main',
                      color: 'success.main',
                      backgroundColor: 'success.50',
                      fontWeight: 'medium',
                    }}
                  />
                </Box>
              </Paper>
              </Grid>
            ))}
        </Grid>
      )}
    </Container>
  );
};

export default TripManager;